import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Sparkles, HandHeart, BookOpen, Church, Home as HomeIcon, Globe } from "lucide-react";
import { IMG } from "../lib/images";
import PageHeader from "../components/PageHeader";
import { Reveal, SectionHeading } from "../components/motion";

const COMMITMENTS = [
  { icon: Heart, title: "Jésus-Christ au centre", text: "Notre foi, notre enseignement et notre espérance sont fondés sur Jésus-Christ." },
  { icon: BookOpen, title: "Une foi enracinée dans la Bible", text: "Nous croyons à la Parole de Dieu et cherchons à la comprendre, la transmettre et la vivre." },
  { icon: Church, title: "Une maison de prière", text: "La prière occupe une place essentielle dans la vie de l'église." },
  { icon: Users, title: "Une communauté fraternelle", text: "Nous ne voulons pas seulement assister à des réunions : nous voulons construire de véritables relations." },
  { icon: HomeIcon, title: "Une place pour chacun", text: "Enfants, jeunes, adultes, couples, célibataires, nouvelles personnes ou croyants de longue date doivent pouvoir trouver leur place." },
  { icon: Globe, title: "Une église tournée vers les autres", text: "Nous voulons témoigner de Jésus-Christ, servir notre prochain et participer au bien de notre territoire." },
];

const PILLARS = [
  { icon: Heart, title: "Aimer Dieu", text: "Développer une relation personnelle avec Jésus-Christ par la Parole, la prière et l'adoration." },
  { icon: Users, title: "Aimer les autres", text: "Construire une communauté marquée par la bienveillance, la compassion, le pardon et l'entraide." },
  { icon: Sparkles, title: "Grandir", text: "Aider chacun à devenir un disciple mature de Jésus-Christ et à découvrir ce que Dieu a placé dans sa vie." },
  { icon: HandHeart, title: "Servir", text: "Porter l'Évangile au-delà des murs de l'église et manifester concrètement l'amour de Dieu autour de nous." },
];

export default function About() {
  return (
    <>
      <PageHeader
        testid="about-header"
        eyebrow="Qui sommes-nous ?"
        titleLines={["Une maison spirituelle,", "ouverte à tous"]}
        intro="La Maison de la Charité est une église chrétienne évangélique à Montévrain / Val d'Europe, centrée sur Jésus-Christ, la Parole de Dieu, la prière et l'amour fraternel."
        image={IMG.about}
      />

      <section className="py-16 sm:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Notre raison d'être" title="Pas une institution. Une maison." />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-stone-600">
                <p>
                  Notre ambition n'est pas de présenter une institution froide ou religieuse, mais une
                  maison spirituelle dans laquelle chacun peut être accueilli, aimé, accompagné et
                  encouragé à grandir avec Jésus-Christ.
                </p>
                <p>
                  Que vous soyez déjà chrétien, en recherche ou simplement curieux, nous croyons qu'il
                  y a une place pour vous ici — sans jugement, sans code à connaître, sans masque à porter.
                </p>
              </div>
              <blockquote className="mt-8 border-l-2 border-terracotta pl-6">
                <p className="verse-quote">« Bien-aimés, aimons-nous les uns les autres ; car l'amour est de Dieu. »</p>
                <cite className="mt-2 block font-mono text-xs uppercase not-italic tracking-[0.2em] text-stone-500">1 Jean 4:7</cite>
              </blockquote>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <img src={IMG.assemblee} alt="L'assemblée de La Maison de la Charité lors d'une rencontre" className="aspect-[4/3] w-full rounded-[2rem] object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <section data-testid="history-section" className="bg-sand py-16 sm:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <SectionHeading eyebrow="Notre histoire" title="De l'évangélisation aux destinées" />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-stone-600">
                <p>
                  La Maison de la Charité est née d'une vision reçue du Seigneur : passer de
                  l'évangélisation à une nouvelle étape, celle de faire des disciples et
                  d'accompagner chacun dans l'accomplissement de sa destinée.
                </p>
                <p>
                  Pendant près de dix années, nous avons parcouru les rues pour annoncer l'Évangile,
                  aller à la rencontre des personnes et partager avec elles l'amour de Dieu et le
                  message de Jésus-Christ.
                </p>
                <p>
                  Puis est venu le temps d'une nouvelle étape. Nous avons reçu la conviction profonde
                  que le Seigneur nous appelait à poursuivre cette mission dans une nouvelle dynamique :
                  ne plus seulement aller à la rencontre des personnes et leur annoncer l'Évangile,
                  mais aussi les accueillir, les accompagner et faire d'elles des disciples de Jésus-Christ.
                </p>
                <p>
                  C'est de cette vision qu'est née La Maison de la Charité : une maison où chacun peut
                  être accueilli, aimé, enseigné et accompagné dans sa marche avec Dieu ; une communauté
                  qui aide chaque personne à grandir dans sa relation avec Jésus-Christ, à découvrir les
                  dons et le potentiel que Dieu a placés en elle, et à avancer vers l'accomplissement de
                  sa destinée.
                </p>
                <p>
                  Nous voulons vivre l'amour de Jésus et évangéliser par l'amour : un amour vrai
                  entre les enfants de Dieu, qui rend visible l'amour de Dieu lui-même. Jésus l'a
                  dit : c'est à l'amour que nous aurons les uns pour les autres que tous
                  reconnaîtront que nous sommes ses disciples.
                </p>
                <p>
                  Nous croyons que l'amour de Dieu ne doit pas seulement être annoncé : il doit être
                  vécu, partagé et rendu visible par nos actions. Notre amour doit être palpable et vrai.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="space-y-4">
            {["Évangéliser", "Faire des disciples", "Accompagner les destinées"].map((t, i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div data-testid={`history-movement-${i}`} className="card-warm flex items-center gap-5 p-6">
                  <span className="font-serif text-3xl font-medium text-terracotta/35">0{i + 1}</span>
                  <p className="font-serif text-xl font-semibold tracking-tight text-obsidian">{t}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="rounded-2xl bg-terracotta p-7 text-white">
                <p className="font-serif text-lg italic leading-snug">« Car Dieu est amour. »</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/75">1 Jean 4:7 — notre identité</p>
              </div>
            </Reveal>
            <Reveal delay={0.38}>
              <div className="rounded-2xl bg-olive-soft p-7">
                <p className="font-serif text-lg italic leading-snug text-olive-deep">
                  « À ceci tous connaîtront que vous êtes mes disciples : si vous avez de l'amour les uns pour les autres. »
                </p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-olive-deep/70">Jean 13:35</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-site">
          <SectionHeading
            eyebrow="Notre vision"
            title="Une maison où l'amour de Dieu devient visible"
            intro="Quatre piliers simples guident tout ce que nous vivons ensemble."
          />
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="h-full">
                <div data-testid={`about-pillar-${i}`} className="flex h-full flex-col bg-white p-7 sm:p-8">
                  <span className="font-serif text-4xl font-medium text-terracotta/30">0{i + 1}</span>
                  <h3 className="mt-5 font-serif text-xl font-semibold tracking-tight text-obsidian">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-site">
          <SectionHeading eyebrow="Nos engagements" title="Ce à quoi nous tenons" />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.08}>
                <div data-testid={`commitment-${i}`} className="card-warm group h-full p-7 transition-transform duration-500 hover:-translate-y-1">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-terracotta-soft text-terracotta transition-colors group-hover:bg-terracotta group-hover:text-white">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-lg font-semibold tracking-tight text-obsidian">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand py-16 sm:py-24">
        <div className="container-site grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="L'équipe" title="Des serviteurs, pas des vedettes" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-stone-600">
                La Maison de la Charité est portée par une équipe de personnes engagées qui servent
                avec simplicité : accueil, louange, enseignement, prière, accompagnement.
              </p>
              <div className="mt-6 rounded-2xl border border-dashed border-line bg-white p-5">
                <p className="text-xs leading-relaxed text-stone-500">
                  <strong className="text-stone-700">Photos et présentation de l'équipe — à venir.</strong>{" "}
                  Les portraits et rôles des responsables seront ajoutés ici prochainement. Aucune
                  fausse identité n'est affichée en attendant.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link data-testid="about-cta-beliefs" to="/ce-que-nous-croyons" className="btn-outline">
                  <BookOpen className="h-4 w-4" />
                  Ce que nous croyons
                </Link>
                <Link data-testid="about-cta-visit" to="/premiere-visite" className="btn-primary">
                  Venir nous rencontrer
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <img src={IMG.volunteers} alt="Bénévoles servant ensemble avec joie" className="aspect-[4/3] w-full rounded-[2rem] object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
